// @ts-nocheck
import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Search, Edit, Calendar, MapPin, User, CheckCircle, X, Eye, Download, FileText } from 'lucide-react';
import { useLocation } from 'wouter';
import { cn } from '@/lib/utils';
import { PrintService } from '../services/print-service';
import { PrintToPDF } from '../components/site-survey/print-to-pdf';

export default function SurveyManagementPage() {
  const [location, setLocation] = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const [showSuccessBanner, setShowSuccessBanner] = useState(false);
  const [highlightedSurveyId, setHighlightedSurveyId] = useState<string | null>(null);
  const [downloadingSurveyId, setDownloadingSurveyId] = useState<string | null>(null);
  const highlightedRowRef = useRef<HTMLTableRowElement>(null);

  const { data: surveys, isLoading, error } = useQuery({
    queryKey: ['/api/site-surveys'],
    retry: 2,
  });

  // Check URL parameters for highlight and from parameters
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const highlightId = params.get('highlight');
    const fromCompletion = params.get('from') === 'completion';

    if (highlightId) {
      setHighlightedSurveyId(highlightId);

      if (fromCompletion) {
        setShowSuccessBanner(true);
        // Auto-hide banner after 5 seconds
        setTimeout(() => setShowSuccessBanner(false), 5000);
      }

      // Clean up URL after processing
      window.history.replaceState({}, '', '/survey-management');
    }
  }, [location]);

  // Scroll to highlighted survey when data loads
  useEffect(() => {
    if (highlightedSurveyId && highlightedRowRef.current) {
      highlightedRowRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Remove highlight after 3 seconds
      setTimeout(() => setHighlightedSurveyId(null), 3000);
    }
  }, [highlightedSurveyId, surveys]);

  const filteredSurveys =
    surveys?.filter(
      (survey: any) =>
        survey.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        survey.installationAddress?.toLowerCase().includes(searchTerm.toLowerCase())
    ) || [];

  const handleEditSurvey = (surveyId: string) => {
    setLocation(`/site-surveys?edit=${surveyId}`);
  };

  const handleViewSurvey = (surveyId: string) => {
    // For now, view mode uses edit mode (can be enhanced later)
    setLocation(`/site-surveys?edit=${surveyId}`);
  };

  const handleDownloadPdf = async (surveyId: string, customerName: string) => {
    if (downloadingSurveyId === surveyId) return; // Prevent multiple simultaneous downloads

    setDownloadingSurveyId(surveyId);
    try {
      // Use the new print service for PDF generation
      await PrintService.printSurvey(surveyId);
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setDownloadingSurveyId(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className="container mx-auto p-4 space-y-6">
      {/* Error Alert */}
      {error && (
        <Alert className="bg-red-50 border-red-200 mb-4">
          <AlertDescription className="text-red-800">
            Failed to load surveys. Please refresh the page or try again later.
          </AlertDescription>
        </Alert>
      )}

      {/* Success Banner */}
      {showSuccessBanner && (
        <Alert className="bg-green-50 border-green-200 mb-4">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800 flex items-center justify-between">
            <span>Site survey completed successfully! The PDF is ready for download.</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowSuccessBanner(false)}
              className="ml-4 p-1 h-auto text-green-600 hover:text-green-800"
            >
              <X className="h-4 w-4" />
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold">Site Survey Management</h1>
          <p className="text-muted-foreground">View and manage all site surveys</p>
        </div>
        <Button onClick={() => setLocation('/site-surveys')}>Create New Survey</Button>
      </div>

      {/* Search Bar */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by customer name or address..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Surveys Table */}
      <Card>
        <CardHeader>
          <CardTitle>Saved Surveys ({filteredSurveys.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8">Loading surveys...</div>
          ) : error ? (
            <div className="text-center py-8 text-red-600">
              <p className="font-semibold">Unable to load surveys</p>
              <p className="text-sm mt-2 text-muted-foreground">
                Please check your connection and try refreshing the page.
              </p>
            </div>
          ) : filteredSurveys.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {searchTerm ? 'No surveys found matching your search.' : 'No surveys saved yet.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Customer</TableHead>
                    <TableHead className="hidden sm:table-cell">Address</TableHead>
                    <TableHead className="hidden md:table-cell">Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-center min-w-[120px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSurveys.map((survey: any) => (
                    <TableRow
                      key={survey.id}
                      ref={highlightedSurveyId === survey.id.toString() ? highlightedRowRef : null}
                      className={cn(
                        'transition-all duration-500',
                        highlightedSurveyId === survey.id.toString() &&
                          'bg-blue-50 ring-2 ring-blue-500 ring-inset'
                      )}
                    >
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <div className="font-medium">{survey.customerName || 'Unknown'}</div>
                            <div className="text-sm text-muted-foreground sm:hidden">
                              {survey.installationAddress}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">
                            {survey.installationAddress || 'No address'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">{formatDate(survey.createdAt)}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={getStatusColor(survey.status)}>
                          {survey.status || 'Draft'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditSurvey(survey.id)}
                            title="Edit Survey"
                            className="hover:bg-blue-50"
                          >
                            <Edit className="h-4 w-4" />
                            <span className="hidden xl:inline ml-1">Edit</span>
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewSurvey(survey.id)}
                            title="View Survey"
                            className="hover:bg-green-50"
                          >
                            <Eye className="h-4 w-4" />
                            <span className="hidden xl:inline ml-1">View</span>
                          </Button>
                          {survey.status === 'completed' && (
                            <PrintToPDF
                              surveyId={survey.id.toString()}
                              buttonText=""
                              buttonVariant="ghost"
                              buttonSize="sm"
                              showIcon={true}
                              className={cn(
                                "hover:bg-purple-50",
                                highlightedSurveyId === survey.id.toString() && 'ring-2 ring-blue-500'
                              )}
                            />
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
